import { useState, useEffect } from "react";
import { useLanguage } from "../LanguageContext";
import { useTheme, PALETTES, PALETTE_KEYS } from "../ThemeContext";
import { Link } from "react-router-dom";
import { 
  Wallet, 
  ArrowRight, 
  Shield, 
  Zap, 
  Users,
  Globe,
  Sparkles,
  ChevronDown,
  Menu,
  X,
  Palette,
  Languages,
  BookOpen
} from "lucide-react";

// Video background
const VIDEO_BG = "https://videos.pexels.com/video-files/857195/857195-hd_1920_1080_25fps.mp4";

// Gold coin images
const COIN_IMAGES = {
  pile1: "https://images.unsplash.com/photo-1643823062948-32438ef15142?auto=format&fit=crop&w=400&q=80",
  pile2: "https://images.unsplash.com/photo-1624365167573-80c5d1b7681f?auto=format&fit=crop&w=400&q=80",
  scattered: "https://images.unsplash.com/photo-1624365169298-a6318708491b?auto=format&fit=crop&w=400&q=80",
  stack: "https://images.unsplash.com/photo-1624365166419-d5d56ee31817?auto=format&fit=crop&w=400&q=80"
};

const LandingPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const { palette, paletteKey, setPaletteKey } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`min-h-screen bg-gradient-to-b ${palette.bg} overflow-hidden`}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrollY > 50 ? `${palette.navSolid} backdrop-blur-lg` : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="logo">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`,
                  boxShadow: `0 10px 30px ${palette.primary}40`
                }}
              >
                <span className="text-slate-900 font-bold text-xl">&#8369;</span>
              </div>
              <span className="text-white font-outfit text-2xl font-bold tracking-tight">
                PIASTRE
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-white/80 hover:text-white transition-colors">
                {language === 'fr' ? 'A Propos' : 'About'}
              </a>
              <a href="#token" className="text-white/80 hover:text-white transition-colors">
                Token
              </a>
              <Link to="/histoire" className="text-white/80 hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-history-link">
                <BookOpen className="w-4 h-4" />
                {language === 'fr' ? 'Histoire' : 'History'}
              </Link>
              
              {/* Language */}
              <button 
                data-testid="language-switcher"
                onClick={toggleLanguage}
                className="px-4 py-2 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors flex items-center gap-2"
              >
                <Languages className="w-4 h-4" />
                {language === 'fr' ? 'EN' : 'FR'}
              </button>
              
              <button 
                data-testid="hero-cta-primary"
                className="px-6 py-3 text-slate-900 rounded-full font-semibold transition-all shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`,
                  boxShadow: `0 10px 30px ${palette.primary}30`
                }}
              >
                {language === 'fr' ? 'Rejoindre' : 'Join Us'}
              </button>
            </div>

            {/* Mobile */}
            <div className="flex items-center gap-3 md:hidden">
              <button onClick={toggleLanguage} className="px-3 py-1.5 rounded-full border border-white/30 text-white text-xs">
                {language === 'fr' ? 'EN' : 'FR'}
              </button>
              <button className="text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 mt-2 border-t border-white/10" data-testid="mobile-menu">
              <div className="flex flex-col gap-3">
                <a href="#about" className="text-white/80 py-2" onClick={() => setIsMenuOpen(false)}>
                  {language === 'fr' ? 'A Propos' : 'About'}
                </a>
                <a href="#token" className="text-white/80 py-2" onClick={() => setIsMenuOpen(false)}>Token</a>
                <Link to="/histoire" className="text-white/80 py-2 flex items-center gap-2" onClick={() => setIsMenuOpen(false)}>
                  <BookOpen className="w-4 h-4" />
                  {language === 'fr' ? 'Histoire' : 'History'}
                </Link>
                <button 
                  className="mt-2 px-6 py-3 text-slate-900 rounded-full font-semibold text-sm"
                  style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}
                >
                  {language === 'fr' ? 'Rejoindre' : 'Join Us'}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* PALETTE SELECTOR — Fixed sidebar (desktop) */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:block">
        <div className="bg-slate-900/80 backdrop-blur-lg rounded-2xl p-3 border border-white/10">
          <div className="flex items-center gap-2 mb-3 px-2">
            <Palette className="w-4 h-4 text-white/60" />
            <span className="text-white/60 text-xs font-medium">
              {language === 'fr' ? 'Thème' : 'Theme'}
            </span>
          </div>
          <div className="space-y-1.5">
            {PALETTE_KEYS.map((key) => {
              const p = PALETTES[key];
              return (
                <button
                  key={key}
                  data-testid={`sidebar-palette-${key}`}
                  onClick={() => setPaletteKey(key)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all ${
                    paletteKey === key ? 'bg-white/20' : 'hover:bg-white/10'
                  }`}
                >
                  <div 
                    className={`w-5 h-5 rounded-full border-2 ${paletteKey === key ? 'border-white' : 'border-white/30'}`}
                    style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.primaryDark})` }}
                  />
                  <span className="text-white text-xs whitespace-nowrap">
                    {language === 'fr' ? p.name.fr : p.name.en}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Palette Selector */}
      <div className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
        <div className="bg-slate-900/90 backdrop-blur-lg rounded-full px-4 py-3 border border-white/10 flex items-center gap-3">
          <Palette className="w-4 h-4 text-white/60" />
          <div className="flex gap-2">
            {PALETTE_KEYS.map((key) => {
              const p = PALETTES[key];
              return (
                <button
                  key={key}
                  data-testid={`mobile-palette-${key}`}
                  onClick={() => setPaletteKey(key)}
                  className={`w-9 h-9 rounded-full border-2 transition-all ${
                    paletteKey === key ? 'border-white scale-110' : 'border-white/30'
                  }`}
                  style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.primaryDark})` }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video autoPlay loop muted playsInline className="absolute w-full h-full object-cover" style={{ filter: 'brightness(0.4)' }}>
            <source src={VIDEO_BG} type="video/mp4" />
          </video>
        </div>
        <div className={`absolute inset-0 bg-gradient-to-b ${palette.bg} opacity-70`} />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float opacity-40"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${4 + Math.random() * 8}px`,
                height: `${4 + Math.random() * 8}px`,
                backgroundColor: palette.primary,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          {/* 3D Coin */}
          <div className="mb-8">
            <div 
              className="w-48 h-48 md:w-64 md:h-64 mx-auto rounded-full relative"
              style={{
                background: `linear-gradient(145deg, ${palette.primary}, ${palette.primaryDark}, ${palette.primary})`,
                boxShadow: `0 0 100px ${palette.primary}50, 0 0 200px ${palette.primary}30, inset 0 4px 20px rgba(255,255,255,0.5), inset 0 -4px 20px rgba(0,0,0,0.3)`,
                animation: 'float 4s ease-in-out infinite'
              }}
            >
              <div 
                className="absolute inset-4 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(145deg, ${palette.primaryDark}, ${palette.primary})`, boxShadow: 'inset 0 4px 15px rgba(0,0,0,0.3)' }}
              >
                <span className="text-6xl md:text-8xl font-serif font-bold" style={{ color: palette.text, textShadow: `2px 2px 4px ${palette.primary}50` }}>
                  &#8369;
                </span>
              </div>
              <svg className="absolute inset-0 w-full h-full animate-spin-slow" viewBox="0 0 200 200">
                <defs><path id="heroCircle" d="M 100,100 m -85,0 a 85,85 0 1,1 170,0 a 85,85 0 1,1 -170,0"/></defs>
                <text style={{ fontSize: '12px', fontWeight: 'bold', letterSpacing: '4px', fill: palette.text, opacity: 0.6 }}>
                  <textPath href="#heroCircle">PIASTRE QUEBEC STELLAR BLOCKCHAIN </textPath>
                </text>
              </svg>
            </div>
          </div>

          <h1 className="font-outfit text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight">
            {language === 'fr' ? 'LE TOKEN' : 'THE'}<br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${palette.primary}, ${palette.accent}, ${palette.primary})` }}>
              {language === 'fr' ? 'QUEBECOIS' : 'QUEBEC TOKEN'}
            </span>
          </h1>

          <div className="max-w-3xl mx-auto mb-10 space-y-4">
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
              {language === 'fr' 
                ? "Piastre est une monnaie communautaire sur la blockchain Stellar qui connecte les utilisateurs WordPress/MyCred au monde de la crypto."
                : "Piastre is a community currency on the Stellar blockchain connecting WordPress/MyCred users to the crypto world."
              }
            </p>
            <p className="text-lg text-white/60">
              {language === 'fr' ? "Un heritage de 400 ans. Une technologie d'avenir." : "A 400-year heritage. Future-forward technology."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button 
              data-testid="hero-cta-wallet"
              className="px-10 py-5 text-slate-900 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-3"
              style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`, boxShadow: `0 20px 40px ${palette.primary}40` }}
            >
              {language === 'fr' ? 'Creer un Portefeuille' : 'Create Wallet'}
              <Wallet className="w-5 h-5" />
            </button>
            <Link 
              to="/histoire"
              data-testid="hero-cta-history"
              className="px-10 py-5 border-2 border-white/30 text-white rounded-full font-bold text-lg hover:bg-white/10 transition-all flex items-center justify-center gap-3"
            >
              {language === 'fr' ? 'Notre Histoire' : 'Our History'}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/50 mx-auto" />
          </div>
        </div>
      </section>

      {/* ═══ ABOUT ═══ */}
      <section id="about" className="relative py-32">
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Token Display */}
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl blur-3xl opacity-30" style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }} />
              <div className={`relative ${palette.cardBg} backdrop-blur border border-white/10 rounded-3xl p-8 md:p-12`}>
                <div className="flex justify-center mb-8">
                  <div 
                    className="w-40 h-40 rounded-full border-4"
                    style={{ background: 'linear-gradient(145deg, #1e3a5f, #0d2137)', borderColor: `${palette.primary}50`, boxShadow: `0 0 60px ${palette.primary}30` }}
                  >
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 100 100" className="w-20 h-20">
                        <defs>
                          <linearGradient id={`goldGrad-${paletteKey}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={palette.primary} />
                            <stop offset="50%" stopColor={palette.accent} />
                            <stop offset="100%" stopColor={palette.primaryDark} />
                          </linearGradient>
                        </defs>
                        <path d="M50 10 C55 20, 65 25, 70 35 C75 45, 70 55, 60 55 L60 70 L75 70 L75 75 L60 75 L60 90 L40 90 L40 75 L25 75 L25 70 L40 70 L40 55 C30 55, 25 45, 30 35 C35 25, 45 20, 50 10 Z" fill={`url(#goldGrad-${paletteKey})`} />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold" style={{ color: palette.primary }}>400+</p>
                    <p className="text-white/60 text-sm">{language === 'fr' ? "Ans d'histoire" : 'Years of history'}</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold" style={{ color: palette.primary }}>0.01$</p>
                    <p className="text-white/60 text-sm">{language === 'fr' ? 'Frais Stellar' : 'Stellar fees'}</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold" style={{ color: palette.primary }}>3 sec</p>
                    <p className="text-white/60 text-sm">Transactions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
                {language === 'fr' ? 'La Monnaie du' : 'The Currency of'}<br />
                <span style={{ color: palette.primary }}>{language === 'fr' ? 'Peuple Quebecois' : 'Quebec People'}</span>
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                {language === 'fr'
                  ? "La piastre est bien plus qu'un mot \u2014 c'est un symbole de notre identite quebecoise qui remonte a la Nouvelle-France. Aujourd'hui, nous la reinventons pour l'ere numerique."
                  : "The piastre is more than just a word \u2014 it's a symbol of Quebec identity dating back to New France. Today, we're reinventing it for the digital age."
                }
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Shield className="w-5 h-5" />, text: language === 'fr' ? 'Securite bancaire' : 'Bank-level security' },
                  { icon: <Zap className="w-5 h-5" />, text: language === 'fr' ? 'Transactions instantanees' : 'Instant transactions' },
                  { icon: <Globe className="w-5 h-5" />, text: language === 'fr' ? 'Compatible WordPress/MyCred' : 'WordPress/MyCred compatible' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${palette.primary}20`, color: palette.primary }}>
                      {item.icon}
                    </div>
                    <span className="text-white">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TOKEN CARDS ═══ */}
      <section id="token" className="relative py-32">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-16">
            {language === 'fr' ? 'Le Token PIASTRE' : 'The PIASTRE Token'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: language === 'fr' ? 'Gagner' : 'Earn', desc: language === 'fr' ? 'Convertissez vos points MyCred en tokens PIASTRE automatiquement.' : 'Convert your MyCred points to PIASTRE tokens automatically.', icon: <Sparkles className="w-8 h-8" /> },
              { title: language === 'fr' ? 'Depenser' : 'Spend', desc: language === 'fr' ? 'Utilisez vos tokens chez les marchands partenaires au Quebec.' : 'Use your tokens at partner merchants in Quebec.', icon: <Wallet className="w-8 h-8" /> },
              { title: language === 'fr' ? 'Communaute' : 'Community', desc: language === 'fr' ? 'Rejoignez une communaute de Quebecois passionnes.' : 'Join a community of passionate Quebecers.', icon: <Users className="w-8 h-8" /> }
            ].map((card, i) => (
              <div 
                key={i}
                data-testid={`token-card-${i}`}
                className={`${palette.cardBg} backdrop-blur border border-white/10 rounded-3xl p-8 hover:scale-105 transition-all duration-300`}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = `${palette.primary}50`}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center text-slate-900" style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}>
                  {card.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{card.title}</h3>
                <p className="text-white/60">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ JOIN US + MOBILE MOCKUP ═══ */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/40" />
        {/* Floating coins */}
        <div className="absolute inset-0 pointer-events-none">
          <img src={COIN_IMAGES.pile1} alt="" className="absolute -left-20 top-20 w-48 h-48 object-cover rounded-full opacity-30 blur-sm" style={{ animation: 'float 6s ease-in-out infinite' }} />
          <img src={COIN_IMAGES.scattered} alt="" className="absolute -right-10 bottom-40 w-40 h-40 object-cover rounded-full opacity-30 blur-sm" style={{ animation: 'float 8s ease-in-out infinite 2s' }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Phone Mockup */}
            <div className="relative flex justify-center">
              <div className="absolute inset-0 blur-3xl opacity-40 rounded-full" style={{ background: `radial-gradient(circle, ${palette.primary}50, transparent)` }} />
              <div className="relative">
                <img src={COIN_IMAGES.pile2} alt="" className="absolute -left-16 -bottom-8 w-28 h-28 object-cover rounded-full shadow-2xl" style={{ animation: 'float 5s ease-in-out infinite', boxShadow: `0 20px 40px ${palette.primary}30` }} />
                <img src={COIN_IMAGES.stack} alt="" className="absolute -right-12 top-1/4 w-24 h-24 object-cover rounded-full shadow-2xl" style={{ animation: 'float 6s ease-in-out infinite 1s', boxShadow: `0 20px 40px ${palette.primary}30` }} />
                
                <div className="relative w-64 h-[500px] rounded-[3rem] p-3 shadow-2xl" style={{ background: 'linear-gradient(145deg, #1a1a1a, #2d2d2d)', boxShadow: `0 40px 80px rgba(0,0,0,0.5), 0 0 60px ${palette.primary}20` }}>
                  <div className="w-full h-full rounded-[2.5rem] overflow-hidden" style={{ background: `linear-gradient(180deg, ${palette.primaryDark}, #0a0a0a)` }}>
                    <div className="p-6 h-full flex flex-col">
                      <div className="flex justify-between items-center mb-8">
                        <span className="text-white/60 text-xs">9:41</span>
                        <div className="flex gap-1">
                          <div className="w-4 h-2 bg-white/60 rounded-sm"></div>
                          <div className="w-4 h-2 bg-white/60 rounded-sm"></div>
                        </div>
                      </div>
                      <div className="text-center mb-8">
                        <p className="text-white/60 text-sm mb-2">{language === 'fr' ? 'Solde Total' : 'Total Balance'}</p>
                        <p className="text-4xl font-bold" style={{ color: palette.primary }}>2,450 &#8369;</p>
                        <p className="text-white/40 text-sm mt-1">= $245.00 CAD</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        {[
                          { icon: '\u2191', label: language === 'fr' ? 'Envoyer' : 'Send' },
                          { icon: '\u2193', label: language === 'fr' ? 'Recevoir' : 'Receive' },
                          { icon: '\u2194', label: language === 'fr' ? 'Echanger' : 'Swap' }
                        ].map((action, i) => (
                          <div key={i} className="flex flex-col items-center p-3 rounded-2xl" style={{ backgroundColor: `${palette.primary}20` }}>
                            <span className="text-xl mb-1" style={{ color: palette.primary }}>{action.icon}</span>
                            <span className="text-white/80 text-xs">{action.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex-1">
                        <p className="text-white/60 text-xs mb-3">{language === 'fr' ? 'Transactions recentes' : 'Recent transactions'}</p>
                        {[
                          { name: 'Cafe Quebec', amount: '-25 &#8369;', neg: true },
                          { name: 'MyCred Reward', amount: '+100 &#8369;', neg: false },
                          { name: 'Transfer', amount: '-50 &#8369;', neg: true }
                        ].map((tx, i) => (
                          <div key={i} className="flex justify-between items-center py-2 border-b border-white/10">
                            <span className="text-white/80 text-sm">{tx.name}</span>
                            <span className="text-sm font-medium" style={{ color: tx.neg ? '#f87171' : '#4ade80' }} dangerouslySetInnerHTML={{ __html: tx.amount }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Join content */}
            <div className="text-center md:text-left">
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
                {language === 'fr' ? 'REJOIGNEZ-NOUS' : 'JOIN US'}
              </h2>
              <p className="text-xl text-white/70 mb-8">
                {language === 'fr'
                  ? "Rejoignez la communaute Piastre et commencez a gagner des recompenses des aujourd'hui!"
                  : "Join the Piastre community and start earning rewards today!"
                }
              </p>
              <div className="max-w-md mx-auto md:mx-0 mb-8">
                <div className="flex gap-3">
                  <input 
                    data-testid="email-signup-input"
                    type="email"
                    placeholder={language === 'fr' ? "Votre courriel" : "Your email"}
                    className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/50 focus:outline-none focus:ring-2"
                    style={{ borderColor: `${palette.primary}30`, focusRingColor: palette.primary }}
                  />
                  <button 
                    data-testid="email-signup-btn"
                    className="px-8 py-4 text-slate-900 rounded-full font-bold hover:opacity-90 transition-all"
                    style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}
                  >
                    {language === 'fr' ? "S'inscrire" : 'Subscribe'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer data-testid="footer" className={`${palette.footerBg} py-16 border-t border-white/10`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}>
                <span className="text-slate-900 font-bold">&#8369;</span>
              </div>
              <span className="text-white font-outfit text-xl font-bold">PIASTRE.ONE</span>
            </div>
            <div className="flex gap-8 text-white/60">
              <Link to="/histoire" className="hover:text-white transition-colors">
                {language === 'fr' ? 'Histoire' : 'History'}
              </Link>
              <Link to="/token" className="hover:text-white transition-colors">
                Token
              </Link>
            </div>
            <p className="text-white/40 text-sm">&copy; 2026 Piastre.one</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
